// Source of truth for the dashboard's widget catalog. Each widget
// is self-contained (pulls its own data); this file pairs ids with
// components, default sizes, scope tabs, and section grouping.

import { isEmpty } from 'lodash'

import { OverviewCardsRow } from './widgets/OverviewCardsRow'
import { FlaggedCriteriaWidget } from './widgets/FlaggedCriteriaWidget'
import { ScoredCallsWidget } from './widgets/ScoredCallsWidget'
import { ScorecardPerformanceTable } from './widgets/ScorecardPerformanceTable'
import { CriteriaTrendsWidget } from './widgets/CriteriaTrendsWidget'
import { AgentPerformanceWidget } from './widgets/AgentPerformanceWidget'
import { AgentPerformanceTableWidget } from './widgets/AgentPerformanceTableWidget'
import { ScoreTrendsWidget } from './widgets/ScoreTrendsWidget'
import { ScoreDistributionWidget } from './widgets/ScoreDistributionWidget'
import { CriteriaPassRateFunnelWidget } from './widgets/CriteriaPassRateFunnelWidget'
import { TopMoversWidget } from './widgets/TopMoversWidget'
import { CallVolumeHeatmapWidget } from './widgets/CallVolumeHeatmapWidget'
import { CriteriaCorrelationHeatmapWidget } from './widgets/CriteriaCorrelationHeatmapWidget'
import { DisputedCriteriaLeaderboardWidget } from './widgets/DisputedCriteriaLeaderboardWidget'
import { SectionPassRateRollupWidget } from './widgets/SectionPassRateRollupWidget'
import { ThresholdSensitivityWidget } from './widgets/ThresholdSensitivityWidget'
import { FailureStreakAlertsWidget } from './widgets/FailureStreakAlertsWidget'
import { PeerLeaderboardWidget } from './widgets/comparative/PeerLeaderboardWidget'
import { ScoreTrendOverlayWidget } from './widgets/comparative/ScoreTrendOverlayWidget'
import { PerEntityDeltaWidget } from './widgets/comparative/PerEntityDeltaWidget'
import { StrengthsWeaknessesWidget } from './widgets/comparative/StrengthsWeaknessesWidget'

// By Campaign was removed entirely (Kyle: "not a QA angle") -- the
// scope-tab machinery lives in git history if it's ever wanted back.
export const SCOPE_TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'scorecard', label: 'By Scorecard' },
  { key: 'agent', label: 'By Agent' },
  { key: 'tag', label: 'By Tag' },
]

// Canonical Bento cell sizes (12-col grid, 24px row unit). Every
// widget picks one so neighbors share a baseline. The user-facing
// resize control cycles SIZE_BUCKETS rather than offering freeform
// resize -- keeps every layout on the shared grid rhythm.
const KPI_BAR = { w: 12, h: 6, minW: 6, minH: 6 }
const HALF_SHORT = { w: 6, h: 10, minW: 4, minH: 6 }
const HALF_TALL = { w: 6, h: 14, minW: 4, minH: 8 }
const FULL_SHORT = { w: 12, h: 10, minW: 6, minH: 6 }
const FULL_TALL = { w: 12, h: 14, minW: 6, minH: 8 }

export const SIZE_BUCKETS = [
  { key: 'half-short', label: 'Half / short', size: HALF_SHORT },
  { key: 'half-tall', label: 'Half / tall', size: HALF_TALL },
  { key: 'full-short', label: 'Full / short', size: FULL_SHORT },
  { key: 'full-tall', label: 'Full / tall', size: FULL_TALL },
]

// The KPI bar cycles its own pair instead of the generic buckets: a
// half-width 12x6 would clip the 5-tile row, so its half form trades
// width for height (tiles wrap 2-up and the row grows tall).
const KPI_BUCKETS = [
  { key: 'kpi-full', label: 'Full bar', size: KPI_BAR },
  // h sized to the wrapped tile grid (3+2 at half width); taller
  // reservations leave dead rows before the next full-width widget.
  { key: 'kpi-half', label: 'Half / tall', size: { w: 6, h: 12, minW: 4, minH: 8 } },
]

const bucketsFor = (widget) => widget?.sizeBuckets || SIZE_BUCKETS

// Widgets whose DEFAULT size is one of their cycleable buckets can be
// resized by the user (widgets may declare their own `sizeBuckets`,
// e.g. the KPI bar's full/half pair).
export const isResizable = (widget) => {
  const defaultLayout = widget?.defaultLayout
  return (
    !!defaultLayout &&
    bucketsFor(widget).some(
      (bucket) => bucket.size.w === defaultLayout.w && bucket.size.h === defaultLayout.h
    )
  )
}

// Best bucket in the widget's cycle for a TARGET size -- used by
// drop-to-adopt: dragging a widget onto a differently-sized one takes
// the target's slot size (a 12-wide can't land "in" a 6-wide slot
// otherwise; the grid just re-flows back to how it looked). Matches
// by width, then nearest height. Null when the widget has no bucket
// at that width.
export const closestBucketForSize = (widget, w, h) => {
  const sameWidth = bucketsFor(widget).filter((bucket) => bucket.size.w === w)
  if (isEmpty(sameWidth)) {
    return null
  }
  return sameWidth.reduce((best, bucket) =>
    Math.abs(bucket.size.h - h) < Math.abs(best.size.h - h) ? bucket : best
  )
}

// Next bucket in the widget's cycle for the given (w, h). Unknown
// sizes start the cycle at the first bucket.
export const nextSizeBucket = (w, h, widget) => {
  const buckets = bucketsFor(widget)
  const idx = buckets.findIndex((bucket) => bucket.size.w === w && bucket.size.h === h)
  return buckets[(idx + 1) % buckets.length]
}

export const WIDGETS = [
  {
    id: 'overview_cards',
    displayName: 'Overview Cards',
    section: 'at-a-glance',
    band: 'detail',
    scopedBy: ['overview', 'scorecard', 'agent', 'tag'],
    Component: OverviewCardsRow,
    defaultLayout: KPI_BAR,
    sizeBuckets: KPI_BUCKETS,
  },

  // Pass rate vs cohort -- sits right under the KPI strip on each
  // scope tab so the comparative read is the first thing below the
  // headline numbers.
  // Pass rate vs cohort + Score trend vs cohort sit side-by-side
  // under the KPI strip on each scope tab. Same section + same band
  // so the bento grid can lay them out as a single row of two
  // half-width cards.
  {
    id: 'comparative_peers_scorecard',
    displayName: 'Pass rate vs average',
    section: 'at-a-glance',
    band: 'detail',
    scopedBy: ['scorecard'],
    Component: PeerLeaderboardWidget,
    defaultLayout: HALF_TALL,
  },
  {
    id: 'comparative_trend_scorecard',
    displayName: 'Score trend vs average',
    section: 'at-a-glance',
    band: 'detail',
    scopedBy: ['scorecard'],
    Component: ScoreTrendOverlayWidget,
    defaultLayout: HALF_TALL,
  },
  {
    id: 'comparative_peers_agent',
    displayName: 'Pass rate vs average',
    section: 'at-a-glance',
    band: 'detail',
    scopedBy: ['agent'],
    Component: PeerLeaderboardWidget,
    defaultLayout: HALF_TALL,
  },
  {
    id: 'comparative_trend_agent',
    displayName: 'Score trend vs average',
    section: 'at-a-glance',
    band: 'detail',
    scopedBy: ['agent'],
    Component: ScoreTrendOverlayWidget,
    defaultLayout: HALF_TALL,
  },
  {
    id: 'comparative_peers_tag',
    displayName: 'Pass rate vs average',
    section: 'at-a-glance',
    band: 'detail',
    scopedBy: ['tag'],
    Component: PeerLeaderboardWidget,
    defaultLayout: HALF_TALL,
  },
  {
    id: 'comparative_trend_tag',
    displayName: 'Score trend vs average',
    section: 'at-a-glance',
    band: 'detail',
    scopedBy: ['tag'],
    Component: ScoreTrendOverlayWidget,
    defaultLayout: HALF_TALL,
  },
  // Strengths & Weaknesses lives in at-a-glance on By Agent (Kyle's
  // feedback: "bumped to the top" -- coaches want the summary before
  // the detail widgets). Agent-only; relies on per-agent
  // personal_failure_rate from the by_criteria endpoint.
  {
    id: 'comparative_strengths_agent',
    displayName: 'Strengths & weaknesses',
    section: 'at-a-glance',
    band: 'comparative',
    scopedBy: ['agent'],
    Component: StrengthsWeaknessesWidget,
    defaultLayout: FULL_SHORT,
  },

  {
    id: 'score_trends',
    displayName: 'Score Trends',
    section: 'over-time',
    band: 'detail',
    scopedBy: ['overview', 'scorecard', 'agent', 'tag'],
    Component: ScoreTrendsWidget,
    defaultLayout: FULL_TALL,
  },

  {
    id: 'call_volume_heatmap',
    displayName: 'Scored Calls by Hour of Week',
    section: 'over-time',
    band: 'detail',
    scopedBy: ['overview', 'agent', 'tag'],
    Component: CallVolumeHeatmapWidget,
    defaultLayout: FULL_SHORT,
  },

  {
    id: 'failure_streak_alerts',
    displayName: 'Failure Streak Alerts',
    section: 'alerts',
    band: 'detail',
    scopedBy: ['overview', 'agent'],
    hiddenInAgentMode: true,
    Component: FailureStreakAlertsWidget,
    defaultLayout: FULL_SHORT,
  },

  {
    id: 'scorecard_performance',
    displayName: 'Scorecard Performance',
    section: 'by-scorecard',
    band: 'detail',
    scopedBy: ['scorecard'],
    Component: ScorecardPerformanceTable,
    defaultLayout: FULL_TALL,
  },

  {
    id: 'criteria_trends',
    displayName: 'Criteria Trends',
    section: 'by-scorecard',
    band: 'detail',
    scopedBy: ['scorecard'],
    Component: CriteriaTrendsWidget,
    defaultLayout: FULL_TALL,
  },

  {
    id: 'criteria_correlation',
    displayName: 'Criteria Co-Failure',
    section: 'by-scorecard',
    band: 'detail',
    scopedBy: ['scorecard'],
    Component: CriteriaCorrelationHeatmapWidget,
    defaultLayout: HALF_TALL,
  },

  {
    id: 'threshold_sensitivity',
    displayName: 'Threshold Sensitivity',
    section: 'by-scorecard',
    band: 'detail',
    scopedBy: ['scorecard'],
    Component: ThresholdSensitivityWidget,
    defaultLayout: HALF_TALL,
  },

  {
    id: 'section_pass_rate_rollup',
    displayName: 'Section Pass Rate Rollup',
    section: 'by-section',
    band: 'detail',
    scopedBy: ['scorecard'],
    Component: SectionPassRateRollupWidget,
    defaultLayout: HALF_TALL,
  },

  {
    id: 'flagged_criteria',
    displayName: 'Flagged Agents per Criteria',
    section: 'by-criterion',
    band: 'detail',
    scopedBy: ['overview', 'agent', 'tag'],
    Component: FlaggedCriteriaWidget,
    // Five columns plus the infractor accordion; a half-width cell
    // squeezes the criterion and scorecard names down to ellipses.
    defaultLayout: FULL_TALL,
  },

  {
    id: 'criteria_funnel',
    displayName: 'Criteria Pass Funnel',
    section: 'by-criterion',
    band: 'detail',
    scopedBy: ['scorecard'],
    Component: CriteriaPassRateFunnelWidget,
    defaultLayout: HALF_TALL,
  },

  {
    id: 'agent_performance',
    displayName: 'Agent Performance',
    section: 'by-agent',
    band: 'detail',
    scopedBy: ['scorecard'],
    Component: AgentPerformanceWidget,
    // Widened to FULL_TALL now that the widget paginates rather than
    // showing an internal scroll bar. Full-width leaves room for the
    // long agent names + call-count column without truncation.
    defaultLayout: FULL_TALL,
  },

  {
    id: 'agent_performance_table',
    displayName: 'Agent Performance (detailed)',
    section: 'by-agent',
    band: 'detail',
    // Overview only: the ported legacy table is the landing-page view
    // users asked for; the bar-chart agent_performance stays on the
    // scorecard tab. Same reportingByUser data (already fetched here,
    // as score_distribution reads it too), so no new plumbing.
    scopedBy: ['overview'],
    Component: AgentPerformanceTableWidget,
    // Full width for the long agent names + call-count columns; tall
    // so the paginated table has room without an internal scroll bar.
    defaultLayout: FULL_TALL,
  },

  {
    id: 'top_movers',
    displayName: 'Top + Bottom Agents',
    section: 'by-agent',
    band: 'detail',
    scopedBy: ['overview', 'scorecard'],
    hiddenInAgentMode: true,
    Component: TopMoversWidget,
    defaultLayout: HALF_TALL,
  },

  {
    id: 'score_distribution',
    displayName: 'Agents by Average Score',
    section: 'at-a-glance',
    band: 'detail',
    scopedBy: ['overview', 'scorecard'],
    hiddenInAgentMode: true,
    Component: ScoreDistributionWidget,
    defaultLayout: HALF_TALL,
  },

  {
    id: 'disputed_criteria_leaderboard',
    displayName: 'Dispute overturn rate',
    section: 'disputes',
    band: 'detail',
    // Now full-width since Dispute Overturn Rate was merged in.
    scopedBy: ['overview'],
    Component: DisputedCriteriaLeaderboardWidget,
    defaultLayout: FULL_TALL,
  },
  // Scored-calls table promoted from the page-header Browse Calls
  // button to a first-class widget (Kyle: "just make the widget calls
  // table"). Available on every tab; scoped to the picker on scope
  // tabs, unscoped on Overview.
  {
    id: 'scored_calls',
    displayName: 'Call scores',
    section: 'over-time',
    band: 'detail',
    scopedBy: ['overview', 'scorecard', 'agent', 'tag'],
    Component: ScoredCallsWidget,
    defaultLayout: FULL_TALL,
  },

  // Per-criterion vs average -- kept on agent scope where the picked
  // entity really is compared against a distinct cohort. Killed on
  // scorecard (comparing to itself). Tag scope skipped: the criteria
  // endpoint's top_infractors are keyed by user_id, would need
  // resolution back through user_tags to identify the tag's entity row.
  {
    id: 'comparative_per_criterion_agent',
    displayName: 'Per-criterion vs average',
    section: 'by-criterion',
    band: 'comparative',
    scopedBy: ['agent'],
    Component: PerEntityDeltaWidget,
    defaultLayout: FULL_TALL,
  },
]

export const SECTIONS = [
  { key: 'at-a-glance', name: 'At a glance' },
  { key: 'over-time', name: 'Over time' },
  { key: 'alerts', name: 'Alerts' },
  { key: 'by-scorecard', name: 'By scorecard' },
  { key: 'by-section', name: 'By section' },
  { key: 'by-criterion', name: 'By criterion' },
  { key: 'by-agent', name: 'By agent' },
  { key: 'disputes', name: 'Disputes' },
]

export const widgetsForTab = (tabKey) =>
  WIDGETS.filter((widget) => Array.isArray(widget.scopedBy) && widget.scopedBy.includes(tabKey))

export const WIDGET_BY_ID = Object.fromEntries(WIDGETS.map((widget) => [widget.id, widget]))

// Each section keeps its own cursor because each renders in a fresh
// grid; a widget at x:6 in a "global" walk would inherit that x in
// the next section, leaving a 6-col gutter on the left.
export const packLayoutForWidgets = (widgets) => {
  const perSection = new Map()
  const getCursor = (key) => {
    if (!perSection.has(key)) {
      perSection.set(key, { cursorY: 0, rowX: 0, rowMaxH: 0 })
    }
    return perSection.get(key)
  }
  return widgets.map((widget) => {
    const cur = getCursor(widget.section)
    const widgetW = widget.defaultLayout.w
    const widgetH = widget.defaultLayout.h
    if (cur.rowX > 0 && cur.rowX + widgetW > 12) {
      cur.cursorY += cur.rowMaxH
      cur.rowX = 0
      cur.rowMaxH = 0
    }
    const layout = {
      i: widget.id,
      x: cur.rowX,
      y: cur.cursorY,
      w: widgetW,
      h: widgetH,
      minW: widget.defaultLayout.minW,
      minH: widget.defaultLayout.minH,
    }
    cur.rowX += widgetW
    cur.rowMaxH = Math.max(cur.rowMaxH, widgetH)
    if (cur.rowX >= 12) {
      cur.cursorY += cur.rowMaxH
      cur.rowX = 0
      cur.rowMaxH = 0
    }
    return { id: widget.id, visible: true, layout }
  })
}
